import { useState, useEffect, useCallback, forwardRef, useRef } from 'react';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import debounce from 'lodash/debounce';
import { FaPencilAlt } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';

import { Modal, ModalMethods, Text, Loader, toasts } from '../../../../ui-component';
import { api } from '../../../../utils/apiRequest';
import { getValueFromLocalStorage } from '../../../../utils/common';

export type ReadymadeTag = {
  id: number;
  name: string;
};

type ReadymadeTagsModalProps = {
  selectedTags: ReadymadeTag[];
  onChange: (tags: ReadymadeTag[]) => void;
};

const ReadymadeTagsModal = forwardRef<ModalMethods, ReadymadeTagsModalProps>(
  ({ selectedTags, onChange }, ref) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [tags, setTags] = useState<ReadymadeTag[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [editingTag, setEditingTag] = useState<ReadymadeTag | null>(null);
    const [editingName, setEditingName] = useState('');

    const fetchTags = useCallback(async (query: string = '') => {
      const boutiqueId = getValueFromLocalStorage('boutique_id');

      if (!boutiqueId) {
        toasts('error', 'Boutique ID not found', 'readymade-tags-boutique-id-missing');

        return;
      }

      setIsLoading(true);

      try {
        const encodedQuery = encodeURIComponent(query.trim());
        const response = await api.getRequest(`readymade/tags?query=${encodedQuery || ''}`);

        if (response.status && !_isNil(response.data)) {
          const data = Array.isArray(response.data) ? response.data : [];
          const valid = data.filter(
            (item: ReadymadeTag) => item.name && !_isEmpty(item.name.trim())
          );

          setTags(valid);
        } else {
          toasts('error', response.message || 'Failed to fetch tags', 'readymade-tags-load-error');
          setTags([]);
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'readymade-tags-load-error');
        }
        setTags([]);
      } finally {
        setIsLoading(false);
      }
    }, []);

    const debouncedFetchRef = useRef(
      debounce((query: string) => {
        void fetchTags(query);
      }, 500)
    );

    useEffect(() => {
      const trimmed = searchQuery.trim();

      if (_isEmpty(trimmed)) {
        debouncedFetchRef.current.cancel();
        void fetchTags('');
      } else {
        debouncedFetchRef.current(trimmed);
      }
    }, [searchQuery, fetchTags]);

    useEffect(() => {
      return () => {
        debouncedFetchRef.current.cancel();
      };
    }, []);

    const toggleTag = (tag: ReadymadeTag) => {
      const exists = selectedTags.some((t) => t.id === tag.id);
      const updated = exists ? selectedTags.filter((t) => t.id !== tag.id) : [...selectedTags, tag];

      onChange(updated);
    };

    const handleCreate = async () => {
      const trimmed = searchQuery.trim();

      if (_isEmpty(trimmed)) {
        toasts('error', 'Please enter a tag name', 'readymade-tag-name-required');

        return;
      }

      const boutiqueId = getValueFromLocalStorage('boutique_id');

      if (!boutiqueId) {
        toasts('error', 'Boutique ID not found', 'readymade-tags-boutique-id-missing');

        return;
      }

      setIsCreating(true);

      try {
        const response = await api.postRequest('readymade/tags', {
          name: trimmed,
        });

        if (response.status && !_isNil(response.data)) {
          const tag = response.data as ReadymadeTag;
          setTags((prev) => [...prev, tag]);
          onChange([...selectedTags, tag]);
          toasts('success', 'Tag created successfully', 'readymade-tag-create-success');
        } else {
          toasts('error', response.message || 'Failed to create tag', 'readymade-tag-create-error');
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'readymade-tag-create-error');
        }
      } finally {
        setIsCreating(false);
      }
    };

    const startEdit = (tag: ReadymadeTag) => {
      setEditingTag(tag);
      setEditingName(tag.name);
    };

    const handleEditSave = async () => {
      if (!editingTag) {
        return;
      }

      const trimmed = editingName.trim();

      if (_isEmpty(trimmed)) {
        toasts('error', 'Please enter a tag name', 'readymade-tag-edit-name-required');

        return;
      }

      try {
        const response = await api.putRequest(`readymade/tags/${editingTag.id}`, {
          name: trimmed,
        });

        if (response.status) {
          const updatedTag: ReadymadeTag = { ...editingTag, name: trimmed };
          setTags((prev) => prev.map((t) => (t.id === updatedTag.id ? updatedTag : t)));
          onChange(selectedTags.map((t) => (t.id === updatedTag.id ? updatedTag : t)));
          setEditingTag(null);
          setEditingName('');
          toasts('success', 'Tag updated successfully', 'readymade-tag-update-success');
        } else {
          toasts('error', response.message || 'Failed to update tag', 'readymade-tag-update-error');
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'readymade-tag-update-error');
        }
      }
    };

    const handleDelete = async (tag: ReadymadeTag) => {
      const confirmed = window.confirm('Are you sure you want to delete this tag?');

      if (!confirmed) {
        return;
      }

      try {
        const response = await api.deleteRequest(`readymade/tags/${tag.id}`);

        if (response.status) {
          setTags((prev) => prev.filter((t) => t.id !== tag.id));
          onChange(selectedTags.filter((t) => t.id !== tag.id));
          toasts('success', 'Tag deleted successfully', 'readymade-tag-delete-success');
        } else {
          toasts('error', response.message || 'Failed to delete tag', 'readymade-tag-delete-error');
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'readymade-tag-delete-error');
        }
      }
    };

    const isQueryExisting = tags.some(
      (item) => item.name.toLowerCase() === searchQuery.trim().toLowerCase()
    );

    const showCreate =
      !_isEmpty(searchQuery.trim()) && !isQueryExisting && !isLoading && !isCreating;

    return (
      <Modal ref={ref} size="large" title="Select or add tags" showFooter={false} showCloseIcon>
        <div className="flex flex-col h-full min-h-[420px]">
          <div className="mb-3">
            <Text className="font-inter text-sm text-gray-600 mb-2 mt-1 block" color="black">
              Add multiple tags to help you search and filter items.
            </Text>
            <input
              type="text"
              placeholder="Search or type a new tag"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="w-full h-10 px-4 border border-gray-300 rounded-lg text-sm font-inter outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500"
              autoFocus
            />
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <Loader />
            </div>
          ) : tags.length > 0 ? (
            <div className="flex flex-col gap-2 py-2 max-h-[260px] overflow-y-auto overflow-x-hidden">
              {tags.map((tag) => {
                const isSelected = selectedTags.some((t) => t.id === tag.id);

                return (
                  <div
                    key={tag.id}
                    className={`px-4 py-3 rounded-lg flex items-center justify-between gap-3 border cursor-pointer transition-all ${
                      isSelected ? 'bg-blue-50 border-blue-500' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                    onClick={() => toggleTag(tag)}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-2 h-2 rounded-full ${
                          isSelected ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      />
                      <Text size="medium" color="black" className="font-inter truncate">
                        {tag.name}
                      </Text>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          startEdit(tag);
                        }}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <FaPencilAlt size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          void handleDelete(tag);
                        }}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <MdDelete size={18} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center h-[220px] p-5">
              <Text color="gray" size="small">
                {searchQuery ? 'No tags found' : 'No tags available'}
              </Text>
            </div>
          )}

          {showCreate && (
            <div className="mt-2">
              <button
                type="button"
                onClick={handleCreate}
                disabled={isCreating}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold transition-all hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? 'Creating...' : `+ Create "${searchQuery.trim()}" as new Tag`}
              </button>
            </div>
          )}

          {editingTag && (
            <div className="mt-4 pt-3 border-t border-gray-200">
              <Text className="font-inter text-xs text-gray-500 mb-1" color="black">
                Editing tag: {editingTag.name}
              </Text>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={editingName}
                  onChange={(event) => setEditingName(event.target.value)}
                  className="flex-1 h-9 px-3 border border-gray-300 rounded-lg text-sm font-inter outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={handleEditSave}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingTag(null);
                    setEditingName('');
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    );
  }
);

ReadymadeTagsModal.displayName = 'ReadymadeTagsModal';

export default ReadymadeTagsModal;
